import React, { useState, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { preloadImage } from '../utils/imagePreloader';

const ImageWithFallback = ({ src, alt, className, fallback, priority = false, ...props }) => {
  const [imgSrc, setImgSrc] = useState(src); // Always start with src for immediate loading
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const preloadedRef = useRef(false);
  
  // Use Intersection Observer for better lazy loading with earlier trigger
  const { ref, inView } = useInView({
    threshold: 0.01,
    triggerOnce: true,
    rootMargin: priority ? '0px' : '1000px', // Start loading 1000px before entering viewport
    skip: priority, // Skip lazy loading for priority images
  });

  // Load images immediately - aggressive preloading strategy
  useEffect(() => {
    if (!preloadedRef.current && src) {
      // Always set src immediately for faster loading
      setImgSrc(src);
      setIsLoading(true);
      
      // Preload with appropriate priority
      if (priority) {
        preloadImage(src, { priority: 'high' }).catch(() => {});
      } else {
        preloadImage(src, { priority: 'auto' }).catch(() => {});
      }
      
      preloadedRef.current = true;
    }
  }, [src, priority]);

  const handleError = () => {
    if (!hasError && fallback) {
      setHasError(true);
      setImgSrc(fallback);
      setIsLoading(false);
    } else if (!hasError) {
      setHasError(true);
      // Create a placeholder if no fallback
      const placeholder = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="800" height="500" viewBox="0 0 800 500">
          <rect width="800" height="500" fill="#f8f9fa"/>
          <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-family="Arial" font-size="24" fill="#999">Image not available</text>
        </svg>
      `)}`;
      setImgSrc(placeholder);
      setIsLoading(false);
    }
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  // Always render img tag immediately for better browser optimization
  return (
    <div ref={priority ? null : ref} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {isLoading && imgSrc && (
        <div className="image-loading" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'loading 1.5s infinite',
          zIndex: 1,
          pointerEvents: 'none',
        }} />
      )}
      {(imgSrc || src) && (
        <img
          src={imgSrc || src}
          alt={alt}
          className={className}
          onError={handleError}
          onLoad={handleLoad}
          style={{ 
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.3s ease',
            width: '100%',
            height: '100%',
            display: 'block',
          }}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          crossOrigin={src && src.startsWith('http') ? 'anonymous' : undefined}
          {...props}
        />
      )}
      <style>{`
        @keyframes loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default ImageWithFallback;
